import { Box, Button, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';
import { startTransition, useEffect, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { ExpandIcon } from '../ExpandIcon';
import { ListBox } from './ListBox';

type SortOption<T> = {
  value: T;
  label: string;
};

type sortByStoreProps<TSortValue, TFilterOptions extends string> = <T>(
  selector: (state: {
    sortBy: TSortValue;
    openedFilters: {
      normalBar: Record<TFilterOptions, boolean>;
      stickyBar: Record<TFilterOptions, boolean>;
    };
    handleOpenedFilters: (
      filter: TFilterOptions,
      bar: 'normalBar' | 'stickyBar',
      state: boolean,
    ) => void;
    handleSortBy: (val: TSortValue) => void;
  }) => T,
  equalityFn?: (a: T, b: T) => boolean,
) => T;

type Props<TSortValue, TFilterOptions extends string> = {
  isStickyBar?: boolean;
  scrollBackToMainBar?: () => void;
  sortOptions: readonly SortOption<TSortValue>[];
  useStore: sortByStoreProps<TSortValue, TFilterOptions>;
  sortByLabel?: string;
  filterKey: TFilterOptions;
  sortBySelectEvent?: (
    overrideProps?: Record<string, string | object> | null,
    callback?: (() => void) | null,
  ) => void;
  isFiltersInHeader?: boolean;
};

export const SortByDropdown = <TSortValue extends string, TFilterOptions extends string>({
  isStickyBar,
  scrollBackToMainBar,
  sortOptions,
  useStore,
  sortByLabel = 'Sort By',
  filterKey,
  sortBySelectEvent,
  isFiltersInHeader,
}: Props<TSortValue, TFilterOptions>) => {
  const buttonBorderRadius = isFiltersInHeader ? '8px' : '64px';
  const buttonPaddingY = isFiltersInHeader ? 2 : 1;
  const buttonFullWidth = !!isFiltersInHeader;
  const { sortBy, handleOpenedFilters } = useStore(
    ({ sortBy, handleOpenedFilters }) => ({
      sortBy,
      handleOpenedFilters,
    }),
    shallow,
  );

  const barKey = isStickyBar ? 'normalBar' : 'stickyBar';
  const isOpen = useStore(state => state.openedFilters[barKey]?.[filterKey] ?? false, shallow);

  const buttonAnchor = useRef<HTMLButtonElement | null>(null);
  const isSelected = !!sortBy;

  const [activeIndex, setActiveIndex] = useState(() => {
    const index = sortOptions.findIndex(sortByOption => sortByOption.value === sortBy);
    return index === -1 ? 0 : index;
  });

  const closeDropdown = () => {
    startTransition(() => {
      buttonAnchor.current?.focus();
      handleOpenedFilters(filterKey, barKey, false);
    });
  };

  useEffect(() => {
    const closeOnEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    };
    if (isOpen) window.addEventListener('keydown', closeOnEsc);
    return () => window.removeEventListener('keydown', closeOnEsc);
  }, [isOpen]);

  return (
    <Box>
      <Button
        ref={buttonAnchor}
        onClick={() => handleOpenedFilters(filterKey, barKey, !isOpen)}
        id="sort-by-dropdown-button"
        role="combobox"
        variant="outlined"
        fullWidth={buttonFullWidth}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="sort-by-dropdown-list"
        aria-activedescendant={isOpen ? `option-${sortOptions[activeIndex].value}` : undefined}
        sx={{
          width: buttonFullWidth ? undefined : 'auto',
          minHeight: '40px',
          px: 3,
          borderRadius: buttonBorderRadius,
          border: '1px solid',
          borderWidth: isSelected ? '2px' : '1px',
          backgroundColor: isSelected ? 'greens.light' : 'white',
          borderColor: isSelected ? 'primary.main' : 'neutral.lighter',
          justifyContent: 'space-between',
          textTransform: 'none',
          py: buttonPaddingY,
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'blues.light',
            cursor: 'pointer',
          },
          transition: 'none',
        }}
      >
        <Typography
          sx={{
            color: isSelected ? 'primary.main' : 'text.secondary',
            fontWeight: 500,
            fontSize: '14px',
          }}
        >
          {sortOptions.find(sortByValue => sortByValue.value === sortBy)?.label ?? sortByLabel}
        </Typography>
        <Box
          sx={{
            color: 'text.secondary',
            pl: '8px',
            pt: '2px',
          }}
        >
          <ExpandIcon width={15} />
        </Box>
      </Button>
      <Popper
        open={isOpen}
        popperOptions={{ strategy: 'fixed' }}
        anchorEl={buttonAnchor.current}
        placement="bottom-start"
        sx={{ zIndex: 200 }}
      >
        <ClickAwayListener onClickAway={() => handleOpenedFilters(filterKey, barKey, false)}>
          <Paper
            square
            elevation={3}
            sx={{
              borderRadius: '8px',
              width: '100%',
              mt: 2,
            }}
          >
            <ListBox
              isOpen={isOpen}
              closeDropdown={closeDropdown}
              activeIndex={activeIndex}
              setActiveIndex={setActiveIndex}
              scrollBackToMainBar={scrollBackToMainBar}
              sortOptions={sortOptions}
              useStore={useStore}
              sortByLabel={sortByLabel}
              sortBySelectEvent={sortBySelectEvent}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
